import { useHasRoles } from 'src/modules/shared/hooks/auth';
import { useCurrentUser } from 'hooks/users';
import { useState, useEffect, useCallback } from 'react';
import { User, UserKind } from 'src/api/generated';
import { formatFullName } from '../../utils/formatting/name.util';

interface UseSelectUsersOptions {
  autoSelectCurrentUser?: boolean;
  clearSelectionForAdmin?: boolean;
}

interface UseSelectUsersProps {
  users: User[];
  options?: UseSelectUsersOptions;
}

export interface UseUsersSelectResult {
  currentUser: User | null;
  selectedUserIds: number[];
  setSelectedUserIds: (userIds: number[]) => void;
  userOptions: { value: number; label: string; kind: UserKind }[];
  isAdmin: boolean;
  loading: boolean;
  resetToDefaults: () => void;
}

export const useUsersSelect = ({
  users,
  options = {
    autoSelectCurrentUser: true,
    clearSelectionForAdmin: true,
  },
}: UseSelectUsersProps) => {
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const { currentUser, loading: loadingCurrentUser } = useCurrentUser();
  const { hasRole: isAdmin, loading: loadingIsAdmin } = useHasRoles('Admin');

  const resetToDefaults = useCallback(() => {
    if (!currentUser || !options.autoSelectCurrentUser) {
      setSelectedUserIds([]);
      return;
    }

    if (isAdmin && options.clearSelectionForAdmin) {
      setSelectedUserIds([]);
      return;
    }

    setSelectedUserIds([currentUser.id]);
  }, [
    options.autoSelectCurrentUser,
    options.clearSelectionForAdmin,
    isAdmin,
    currentUser,
  ]);

  useEffect(() => {
    resetToDefaults();
  }, [resetToDefaults]);

  const userOptions = users.map((user) => ({
    value: user.id,
    label: formatFullName(user.firstName, user.lastName, user.email),
    kind: user.kind,
  }));

  const handleUserIdsChange = (values: (string | number)[]) => {
    setSelectedUserIds(values.map((v) => Number(v)));
  };

  return {
    currentUser,
    selectedUserIds,
    setSelectedUserIds: handleUserIdsChange,
    userOptions,
    isAdmin,
    loading: loadingCurrentUser || loadingIsAdmin,
    resetToDefaults,
  };
};
